<template>
    <div class="dashboard-bar">
        <RouterLink :to="{ name: dashboardRoutesNames.GUILDS_STUDIO_ROUTE_NAME }"
                    class="dashboard-bar__brand">
            Guilds <span class="dashboard-bar__text--dark"> Studio</span>
        </RouterLink>
        <div class="dashboard-bar__items">
            <template v-if="isGuildStudioPage">
                <span class="dashboard-bar__text--dark dashboard-bar__separator">
                    |
                </span>
                <DashboardBarItem :link="guildCreateLink"
                                  iconClass="fa-solid fa-plus"
                                  text="Create New Guild" />
            </template>
            <template v-if="isGuildPage && isGuildOwner">
                <span class="dashboard-bar__text--dark dashboard-bar__separator">
                    |
                </span>
                <DashboardBarItem :link="guildEditLink"
                                  iconClass="fa-solid fa-pencil"
                                  text="Edit Guild" />
                <span class="dashboard-bar__text--dark dashboard-bar__separator">
                    |
                </span>
                <DashboardBarItem :link="missionCreateLink"
                                  iconClass="fa-solid fa-plus"
                                  text="Create New Mission" />
            </template>
            <template v-if="isMissionPage && isGuildOwner">
                <span class="dashboard-bar__text--dark dashboard-bar__separator">
                    |
                </span>
                <DashboardBarItem :link="missionEditLink"
                                  iconClass="fa-solid fa-pencil"
                                  text="Edit Mission" />
                <span class="dashboard-bar__text--dark dashboard-bar__separator">
                    |
                </span>
                <template v-if="isLuckyDrawCompleted">
                    <DashboardBarItem text="Relaunch Lucky Draw"
                                      iconClass="fa-solid fa-rotate-right"
                                      @click.stop="handleLuckyDrawRestart" />
                    <span class="dashboard-bar__text--dark dashboard-bar__separator">
                        |
                    </span>
                </template>
                <DashboardBarItem :link="roundsLink"
                                  iconClass="fa-solid fa-list"
                                  :text="dashboardStore.hasMissionRounds ? 'Edit Rounds' : 'Add Rounds'" />
                <span class="dashboard-bar__text--dark dashboard-bar__separator">
                    |
                </span>
                <DashboardBarItem text="Show Winners"
                                  iconClass="fa-regular fa-gift"
                                  @click.stop="handleOwnerLuckyDrawWinnersClick"/>

                <template v-if="wasMissionPublished">
                    <span class="dashboard-bar__text--dark dashboard-bar__separator">
                        |
                    </span>

                    <template v-if="isPreview">
                        <DashboardBarItem text="Switch to Live"
                                          @click.stop="openLiveMission">
                            <span class="text-icon">Preview</span>
                        </DashboardBarItem>
                    </template>
                    <template v-else>
                        <DashboardBarItem text="Switch to Preview"
                                          @click.stop="openDraftMission">
                            <span class="text-icon text-icon--live">Live</span>
                        </DashboardBarItem>
                    </template>
                </template>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import { useRouter } from 'vue-router';
    import DashboardBarItem from '@/js/quests/dashboard/components/dashboard-bar/DashboardBarItem.vue';
    import { FULL_BUILDER_ROUTE_PATHS } from '@/js/pages/builder/routes/builderRoutePaths';
    import { dashboardRoutesNames } from '@/js/quests/dashboard/constants/dashboardRoutesNames';
    import { dashboardRoutesPaths } from '@/js/quests/dashboard/constants/dashboardRoutesPaths';
    import { ModalEnum, useModalStore } from '@/js/store/quests/game/modules/ModalStore';
    import { useLuckyDrawWinnerStore } from '@/js/store/quests/dashboard/LuckyDrawWinnerStore';
    import { useDashboardStore } from '@/js/store/quests/dashboard/DashboardStore';
    import { useGuildOwnership } from '@/js/quests/dashboard/composables/useGuildOwnership';

    const router = useRouter();
    const modalStore = useModalStore();
    const luckyDrawWinnerStore = useLuckyDrawWinnerStore();
    const dashboardStore = useDashboardStore();

    const { setShowOwnerData } = luckyDrawWinnerStore;

    const guildSlug = ref(router.currentRoute.value.params.guildSlug as string);
    const missionSlug = computed(() => router.currentRoute.value.params.slug as string);

    const isGuildStudioPage = computed(() => router.currentRoute.value.name === dashboardRoutesNames.GUILDS_STUDIO_ROUTE_NAME);
    const isGuildPage = computed(() => router.currentRoute.value.name === dashboardRoutesNames.GUILD_ROUTE_NAME);

    const missionRoutes = [dashboardRoutesNames.MISSION_ROUTE_NAME, dashboardRoutesNames.MISSION_PREVIEW_ROUTE_NAME];
    const isMissionPage = computed(() => missionRoutes.includes(router.currentRoute.value.name));
    const isPreview = computed(() => router.currentRoute.value.name === dashboardRoutesNames.MISSION_PREVIEW_ROUTE_NAME);

    const guildCreateLink = computed(() => FULL_BUILDER_ROUTE_PATHS.GUILD_BUILDER);
    const guildEditLink = computed(() => FULL_BUILDER_ROUTE_PATHS.GUILD_BUILDER_EDIT.replace(':guildSlug', guildSlug.value));
    const missionCreateLink = computed(() => FULL_BUILDER_ROUTE_PATHS.MISSION_INTRO.replace(':guildSlug', guildSlug.value));
    const missionEditLink = computed(() => FULL_BUILDER_ROUTE_PATHS.MISSION_BUILDER_EDIT
        .replace(':guildSlug', guildSlug.value)
        .replace(':missionSlug', missionSlug.value));
    const roundsLink = computed(() => FULL_BUILDER_ROUTE_PATHS.ROUND_BUILDER
        .replace(':guildSlug', guildSlug.value)
        .replace(':missionSlug', missionSlug.value));

    const missionShowLink = computed(() => dashboardRoutesPaths.MISSION_ROUTE_PATH
        .replace(':guildSlug', guildSlug.value)
        .replace(':slug', missionSlug.value));

    const missionPreviewLink = computed(() => dashboardRoutesPaths.MISSION_PREVIEW_ROUTE_PATH
        .replace(':guildSlug', guildSlug.value)
        .replace(':slug', missionSlug.value));

    const isLuckyDrawCompleted = computed(() => {
        if (!dashboardStore.missionData) {
            return false;
        }

        return !!dashboardStore.missionData.data.lucky_draw?.completed_at;
    });

    const wasMissionPublished = computed(() => !!dashboardStore.missionData?.data?.published_at);

    const { isGuildOwner } = useGuildOwnership(guildSlug);

    const handleLuckyDrawRestart = () => {
        modalStore.toggleModal(ModalEnum.LUCKY_DRAW_RESTART, true);
    };

    const handleOwnerLuckyDrawWinnersClick = () => {
        setShowOwnerData();
        modalStore.toggleModal(ModalEnum.LUCKY_DRAW_WINNERS, true);
    };

    const openDraftMission = () => {
        router.push(missionPreviewLink.value);
    };
    const openLiveMission = () => {
        router.push(missionShowLink.value);
    };

    watch(() => router.currentRoute.value.params, () => {
        guildSlug.value = router.currentRoute.value.params.guildSlug as string;
    });
</script>

<style scoped lang="scss">
@use "@/sass/partials/variables" as legacyVariables;
@use "@/sass/missions-partials/z-index" as missionsZIndex;
@use "@/sass/design-system/variables" as variables;

.dashboard-bar {
    position: fixed;
    z-index: missionsZIndex.$dashboard-navigation-wrapper-z-index;
    top: 0;
    display: flex;
    width: 100%;
    height: legacyVariables.$dashboard-bar-height;
    align-items: center;
    padding: 0 8px;
    border-bottom: 1px solid legacyVariables.$dark-theme-border;
    background-color: legacyVariables.$blackcurrant-dark;
    color: variables.$baseWhite;

    &__text {
        &--dark {
            font-weight: 300;
            opacity: 0.5;
        }
    }

    &__separator {
        user-select: none;
    }

    &__brand {
        margin-right: 14px;
        color: variables.$baseWhite;
        font-size: 15px;
        font-weight: 700;
        text-decoration: none;
        transition: opacity 200ms ease-in-out;

        &:hover {
            opacity: 0.7;
        }
    }

    &__items {
        display: flex;
        align-items: center;
        font-size: 14px;
        gap: 16px;
    }
}

.text-icon {
    padding: 2px 6px;
    border-radius: 18px;
    background-color: variables.$gray50;
    color: variables.$baseBlack;
    font-size: 11px;
    font-weight: 700;

    &--live {
        background-color: legacyVariables.$blurred-green;
    }
}
</style>
